import { motion } from "framer-motion";
import { useSettings } from "../contexts/SettingsContext";
import SEO from "../components/SEO";
import SimpleHeader from "../components/SimpleHeader";
import SimpleFooter from "../components/SimpleFooter";

export default function PrivacyPolicy() {
  const { settings } = useSettings();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for de5.ai - Learn how we collect, use, and protect your data when using our DeFi automation platform."
      />
      <SimpleHeader />
      <main className="min-h-screen bg-white dark:bg-[#0b0f19] text-gray-900 dark:text-white py-16 px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            {/* Title */}
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h1
                className="text-4xl sm:text-5xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Our Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Our Privacy Policy outlines how we collect, use, and protect
                your personal information. Your privacy and security are our
                priorities.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Last Updated on August 24, 2024
              </p>
            </motion.div>

            {/* Information We Collect */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Information We Collect
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We may collect personal information from you when you visit
                  our website, register for an account, or interact with our
                  services. This information may include your name, email
                  address, contact details, and any other information you
                  voluntarily provide to us.
                </p>
              </div>
            </motion.section>

            {/* How We Use Your Information */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                How We Use Your Information
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We may use the information we collect from you for various
                  purposes, including:
                </p>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 dark:text-gray-300">
                  <li>Providing and improving our products and services</li>
                  <li>Personalizing your experience on our website</li>
                  <li>
                    Communicating with you about your account and any updates or
                    promotions
                  </li>
                  <li>
                    Analyzing website traffic and user behavior to enhance our
                    offerings
                  </li>
                </ol>
              </div>
            </motion.section>

            {/* Data Security */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Data Security
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We take data security seriously and employ industry-standard
                  measures to protect your personal information from
                  unauthorized access, disclosure, alteration, or destruction.
                  However, no method of transmission over the internet or
                  electronic storage is 100% secure, and we cannot guarantee
                  absolute security.
                </p>
              </div>
            </motion.section>

            {/* Third-Party Disclosure */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Third-Party Disclosure
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We do not sell, trade, or otherwise transfer your personal
                  information to third parties without your consent, except as
                  required by law or as necessary to provide our services. We
                  may share your information with trusted third-party service
                  providers who assist us in operating our website, conducting
                  our business, or servicing you, provided that they agree to
                  keep your information confidential.
                </p>
              </div>
            </motion.section>

            {/* Cookies */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Cookies
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Our website may use cookies to enhance your browsing
                  experience and collect information about how you interact with
                  our site. You can adjust your browser settings to refuse
                  cookies or alert you when cookies are being sent, but some
                  features of the site may not function properly without
                  cookies.
                </p>
              </div>
            </motion.section>

            {/* Changes to Privacy Policy */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Changes to this Privacy Policy
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We reserve the right to update or change this Privacy Policy
                  at any time. Any changes will be posted on this page, and the
                  effective date will be updated accordingly. We encourage you
                  to review this Privacy Policy periodically for any updates.
                </p>
              </div>
            </motion.section>

            {/* Contact Us */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Contact Us
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  If you have any questions or concerns about our Privacy Policy
                  or the handling of your personal information, please contact
                  us at{" "}
                  <a
                    href="mailto:contact@de5.ai"
                    className="font-semibold transition-colors"
                    style={{ color: settings.primaryColor }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    contact@de5.ai
                  </a>
                </p>
              </div>
            </motion.section>

            {/* Back to Home Button */}
            <motion.div variants={itemVariants} className="text-center mt-12">
              <motion.button
                onClick={() => (window.location.href = "/")}
                className="px-8 py-4 rounded-xl font-semibold text-lg text-white shadow-lg hover:shadow-xl transition-all duration-300"
                style={{
                  backgroundColor: settings.primaryColor,
                  boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = settings.primaryColor;
                  e.currentTarget.style.boxShadow =
                    "0 20px 40px rgba(233, 92, 73, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = settings.primaryColor;
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(233, 92, 73, 0.25)";
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Back to Home
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </main>
      <SimpleFooter />
    </>
  );
}
